import {
    Debtor,
    CreateDebtor,
    DeleteDebtorContact,
    CreateDebtorGateway,
    CreateOrUpdateDebtorContactGateway,
    CreateOrUpdateDebtorContact,
    UpsertDebtor,
    UpsertDebtorGateway,
    Contact
} from '../models/debtorModels'
import { Company } from '../models/companyModel'
import { resolveApiUrl } from '../utils/requestsUtils'
import { RequestResponse, requestService } from './requestServices'
import { isEmpty, isNil } from 'lodash'
import { QueryStringInput } from '@hello-aria/query-string'
import { mapCreateOrUpdateDebtorContacts } from '../utils/debtorsUtils'
import { removeEmailWhitespace } from '../utils/inputUtils'
import { DocuSignContract } from '../models/contractModels'
import { trackEvent } from '../utils/analyticsUtils'
import { sanitizeAddress } from '../models/addressModels'

export const postDebtorService = async (
    payload: CreateDebtor,
    companyId: Company['id']
): Promise<RequestResponse<Debtor> | undefined> => {
    const url = resolveApiUrl('debtor.create', { variables: { companyId } })

    const isAddressDefined =
        !isEmpty(payload.address?.first) && !isEmpty(payload.address?.city) && !isEmpty(payload.address?.postal)

    const formattedContacts = (() => {
        if (payload.contacts?.length) {
            return payload.contacts.map((contact) => removeEmailWhitespace(contact))
        }
        if (payload.contact) {
            return [removeEmailWhitespace(payload.contact)]
        }
        return []
    })()

    const formattedPayload: CreateDebtorGateway = {
        country: payload.identifier.country,
        name: payload.name,
        identifier: payload.identifier,
        contacts: formattedContacts,
        address: isAddressDefined
            ? {
                  first: payload.address?.first,
                  city: payload.address?.city,
                  postal: payload.address?.postal,
                  country: payload.address?.country || payload.identifier?.country
              }
            : undefined
    }

    const response = await requestService<Debtor>(url, { method: 'POST', data: formattedPayload })

    trackEvent('Create_Debtor')

    return response
}

interface GetDebtors {
    companyId: string
    query?: QueryStringInput
}

export const getDebtorsService = async (params: GetDebtors): Promise<RequestResponse<Debtor[]> | undefined> => {
    try {
        const url = resolveApiUrl('debtor.getAll', { variables: { companyId: params.companyId }, query: params.query })
        const response = await requestService<Debtor[]>(url)
        return response
    } catch (error) {
        if (error instanceof Error) {
            throw new Error('getDebtorsService: ' + error.message)
        }

        throw new Error('getDebtorsService: ' + error)
    }
}

export const getDebtorByIdService = async (
    debtorId: Debtor['id'] | undefined | null,
    companyId: Company['id']
): Promise<RequestResponse<Debtor> | undefined> => {
    try {
        if (isNil(debtorId)) {
            return
        }
        const url = resolveApiUrl('debtor.get', { variables: { companyId, debtorId } })
        return await requestService<Debtor>(url)
    } catch (error) {
        if (error instanceof Error) {
            throw new Error('getDebtorByIdService: ' + error.message)
        }

        throw new Error('getDebtorByIdService: ' + error)
    }
}

export const deleteDebtorContactService = async (
    companyId: Company['id'],
    payload: DeleteDebtorContact
): Promise<RequestResponse<Debtor> | undefined> => {
    try {
        // We use the create endpoint (upsert) to update the contacts list of the given debtor
        const url = resolveApiUrl('debtor.create', { variables: { companyId } })

        const formattedPayload: CreateDebtorGateway = {
            country: payload.country,
            identifier: payload.identifier,
            name: payload.name,
            contacts: payload.contacts.filter((contact) => contact.email !== payload.emailToDelete)
        }

        const response = await requestService<Debtor>(url, { method: 'POST', data: formattedPayload })

        trackEvent('Update_Debtor_Contact')

        return response
    } catch (error) {
        if (error instanceof Error) {
            throw new Error('deleteDebtorContactService: ' + error.message)
        }

        throw new Error('deleteDebtorContactService: ' + error)
    }
}

export const createOrUpdateContactService = async (
    companyId: Company['id'],
    payload: CreateOrUpdateDebtorContact
): Promise<RequestResponse<Debtor> | undefined> => {
    try {
        const url = resolveApiUrl('debtor.create', { variables: { companyId } })
        const formattedPayload: CreateOrUpdateDebtorContactGateway = mapCreateOrUpdateDebtorContacts(payload)
        const response = await requestService<Debtor>(url, { method: 'POST', data: formattedPayload })

        trackEvent('Update_Debtor_Contact')

        return response
    } catch (error) {
        if (error instanceof Error) {
            throw new Error('createOrUpdateContactService: ' + error.message)
        }

        throw new Error('createOrUpdateContactService: ' + error)
    }
}

function mergeContactsByEmail(baseContacts: Contact[], newContacts: Contact[] = []): Contact[] {
    const merged = new Map([...baseContacts, ...newContacts].map((c) => [c.email, c]))
    return Array.from(merged.values())
}

export const upsertDebtorService = async (
    companyId: Company['id'],
    payload: UpsertDebtor
): Promise<RequestResponse<Debtor> | undefined> => {
    // We use the create endpoint (upsert)
    const url = resolveApiUrl('debtor.create', { variables: { companyId } })

    const contacts: Contact[] = mergeContactsByEmail(payload.existingContacts, payload.contacts)

    const formattedPayload: UpsertDebtorGateway = {
        name: payload.name,
        country: payload.identifier?.country,
        identifier: payload.identifier,
        contacts,
        address: sanitizeAddress(payload.address)
    }

    const response = await requestService<Debtor>(url, { method: 'POST', data: formattedPayload })

    trackEvent('Update_Debtor')

    return response
}

type GetDebtorContractService = {
    companyId: string | undefined
    debtorId: string | null | undefined
}

export const getDebtorContractService = async ({
    companyId,
    debtorId
}: GetDebtorContractService): Promise<RequestResponse<DocuSignContract> | undefined> => {
    try {
        if (!companyId || !debtorId) {
            throw new Error('getDebtorContractService: companyId or debtorId is missing')
        }

        const url = resolveApiUrl('guarantorContract.get', {
            variables: {
                companyId,
                debtorId
            }
        })

        const response = await requestService<DocuSignContract>(url)
        return response
    } catch (error) {
        if (error instanceof Error) {
            throw new Error('getDebtorContractService: ' + error.message)
        }

        throw new Error('getDebtorContractService: ' + error)
    }
}
